import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { pageNotificationsApi, settingsApi } from '@/services/api'
import { X, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'

interface PageNotificationProps {
  pageRoute: string
}

interface DismissedNotification {
  id: number
  dismissedAt: string
  version: string // updated_at timestamp from notification
}

export default function PageNotification({ pageRoute }: PageNotificationProps) {
  const [dismissedNotifications, setDismissedNotifications] = useState<DismissedNotification[]>([])
  const [dismissalDays, setDismissalDays] = useState<number>(7) // Default 7 days

  // Fetch system settings to get dismissal duration
  const { data: settingsData } = useQuery({
    queryKey: ['settings-notification-dismissal'],
    queryFn: () => settingsApi.get(),
    staleTime: 300000, // Cache for 5 minutes
  })

  // Update dismissal days from settings
  useEffect(() => {
    if (settingsData?.data?.settings?.notification_dismissal_days) {
      setDismissalDays(parseInt(settingsData.data.settings.notification_dismissal_days))
    }
  }, [settingsData])

  // Load dismissed notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('dismissedNotifications')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Migrate old format (array of IDs) to new format
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'number') {
          // Old format - convert to new format with current timestamp
          const migrated = parsed.map((id: number) => ({
            id,
            dismissedAt: new Date().toISOString(),
            version: ''
          }))
          setDismissedNotifications(migrated)
          localStorage.setItem('dismissedNotifications', JSON.stringify(migrated))
        } else {
          setDismissedNotifications(parsed)
        }
      } catch (e) {
        console.error('Failed to parse dismissed notifications', e)
      }
    }
  }, [])

  // Fetch notifications for current page AND global (all) notifications
  const { data: pageData } = useQuery({
    queryKey: ['page-notifications', pageRoute],
    queryFn: () => pageNotificationsApi.getByPage(pageRoute),
    staleTime: 60000, // Cache for 1 minute
  })

  const { data: globalData } = useQuery({
    queryKey: ['page-notifications', 'all'],
    queryFn: () => pageNotificationsApi.getByPage('all'),
    staleTime: 60000, // Cache for 1 minute
  })

  const handleDismiss = (id: number, updatedAt: string) => {
    const newDismissed: DismissedNotification = {
      id,
      dismissedAt: new Date().toISOString(),
      version: updatedAt
    }

    // Remove old dismissal for this notification if exists
    const filtered = dismissedNotifications.filter(d => d.id !== id)
    const updated = [...filtered, newDismissed]

    setDismissedNotifications(updated)
    localStorage.setItem('dismissedNotifications', JSON.stringify(updated))
  }

  const pageNotifications = pageData?.data?.notifications || []
  const globalNotifications = globalData?.data?.notifications || []
  const allNotifications = [...globalNotifications, ...pageNotifications]

  // Clean up expired dismissals and filter visible notifications
  const visibleNotifications = allNotifications.filter((notification) => {
    const dismissed = dismissedNotifications.find(d => d.id === notification.id)

    if (!dismissed) {
      return true // Not dismissed, show it
    }

    // Check if notification has been updated since dismissal (version-based)
    if (notification.updated_at && dismissed.version && notification.updated_at !== dismissed.version) {
      // Notification was updated, remove from dismissed list and show it
      const updated = dismissedNotifications.filter(d => d.id !== notification.id)
      setDismissedNotifications(updated)
      localStorage.setItem('dismissedNotifications', JSON.stringify(updated))
      return true
    }

    // Check if dismissal has expired (time-based)
    const dismissedDate = new Date(dismissed.dismissedAt)
    const expirationDate = new Date(dismissedDate.getTime() + dismissalDays * 24 * 60 * 60 * 1000)
    const now = new Date()

    if (now >= expirationDate) {
      // Dismissal expired, remove from dismissed list and show it
      const updated = dismissedNotifications.filter(d => d.id !== notification.id)
      setDismissedNotifications(updated)
      localStorage.setItem('dismissedNotifications', JSON.stringify(updated))
      return true
    }

    return false // Still dismissed
  })

  if (visibleNotifications.length === 0) {
    return null
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info className="w-5 h-5" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />
      case 'success':
        return <CheckCircle className="w-5 h-5" />
      case 'error':
        return <XCircle className="w-5 h-5" />
      default:
        return <Info className="w-5 h-5" />
    }
  }

  const getColors = (type: string) => {
    switch (type) {
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-600',
          button: 'text-blue-600 hover:text-blue-800',
        }
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          icon: 'text-yellow-600',
          button: 'text-yellow-600 hover:text-yellow-800',
        }
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: 'text-green-600',
          button: 'text-green-600 hover:text-green-800',
        }
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: 'text-red-600',
          button: 'text-red-600 hover:text-red-800',
        }
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-600',
          button: 'text-gray-600 hover:text-gray-800',
        }
    }
  }

  return (
    <div className="space-y-3 mb-6">
      {visibleNotifications.map((notification) => {
        const colors = getColors(notification.type)
        return (
          <div
            key={notification.id}
            className={`${colors.bg} ${colors.border} border rounded-lg p-4 flex items-start`}
          >
            <div className={`${colors.icon} flex-shrink-0 mt-0.5`}>
              {getIcon(notification.type)}
            </div>
            <div className={`ml-3 flex-1 ${colors.text}`}>
              <p className="text-sm whitespace-pre-wrap">{notification.message}</p>
            </div>
            <button
              onClick={() => handleDismiss(notification.id, notification.updated_at)}
              className={`ml-3 flex-shrink-0 ${colors.button} transition-colors`}
              aria-label="Dismiss notification"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
